import { cloneDeep, isEmpty, isEqual, merge } from "lodash";
import { getObjValAtPath } from "utils/fields";
import changeDetectionFields from "./priorities.json";

const subCompDifference = ({ modelDataPath = "", subComponentsDiff = {}, val = {}, valToCompare = {} }) => {
    var subCount = 0;
    var updatedSubComponentsDiff = cloneDeep(subComponentsDiff);

    Object.entries(val.subcomponents || {}).forEach(([subComponentType, subList]) => {
        const subComponentsByType = Object.entries(subList).reduce((acc, [subComponentId, subVal]) => {
            const subModelDataPath = `${modelDataPath}.subcomponents.${subComponentType}.${subComponentId}`;
            const subValToCompare = valToCompare.subcomponents?.[subComponentType]?.[subComponentId] || {};

            // If componentId or subcomponentId is missing from valToCompare (prevVal/curVal) count the sub component as added/deleted
            // console.log(valToCompare, subValToCompare, subComponentType)
            if (isEmpty(valToCompare) || isEmpty(subValToCompare)) {
                subCount += 1;

                return ({
                    ...acc,
                    [subComponentId]: { label: subVal.label, path: subModelDataPath, value: subVal }
                });
            }
            return acc;
        }, { ...subComponentsDiff[subComponentType] });

        if (!isEmpty(subComponentsByType)) {
            updatedSubComponentsDiff[subComponentType] = subComponentsByType;
        }
    });

    return {
        subCount,
        updatedSubComponentsDiff
    };
};

const componentDifference = ({ action = "", componentType = "", modelData = {}, modelDataToCompare = {} }) => {
    const parentComponentTypes = ["basement", "wall", "ceiling"];
    var subComponentsDiff = {};
    var count = 0;

    const componentsByType = Object.entries(modelData.components?.[componentType] || {}).reduce((acc, [componentId, val]) => {
        const modelDataPath = `modelData.components.${componentType}.${componentId}`;
        const valToCompare = modelDataToCompare.components?.[componentType]?.[componentId] || {};

        // Check for added/deleted subcomponents to this component
        if (parentComponentTypes.includes(componentType)) {
            const { subCount = 0, updatedSubComponentsDiff = {} } = subCompDifference({ modelDataPath, subComponentsDiff, val, valToCompare });

            subComponentsDiff = updatedSubComponentsDiff;
            count += subCount;
        }

        // Check current model data for this component, missing = deleted
        if (isEmpty(valToCompare)) {
            count += 1;

            return {
                ...acc,
                [componentId]: { label: val.label, path: modelDataPath, value: val }
            }
        }

        return acc;
    }, {});

    return {
        count,
        componentsByType,
        subComponentsDiff,
    }
};

export const detectModelChanges = (prevModelData = {}, curModelData = {}) => {
    var diffScore = 0;

    /* Components Check */
    const mainComponents = { basement: {}, crawlspace: {}, slab: {}, wall: {}, ceiling: {}, expFloor: {} };
    const subComponents = { window: {}, door: {}, floorHeader: {} };

    var deletedComponents = {};
    var numDeleted = 0;

    var addedComponents = {};
    var numAdded = 0;

    Object.keys(mainComponents).forEach((componentType) => {
        // Compare current model data to previous model data to find deleted components
        const {
            count: countDeleted,
            componentsByType: deletedByType,
            subComponentsDiff: deletedSubComponents
        } = componentDifference({
            action: "deleted",
            componentType,
            componentsDiff: deletedComponents,
            modelData: prevModelData,
            modelDataToCompare: curModelData
        });

        numDeleted += countDeleted;
        if (!isEmpty(deletedByType)) {
            deletedComponents[componentType] = deletedByType;
        }
        deletedComponents = merge(deletedComponents, deletedSubComponents);

        // Compare previous model data to current model data to find new components
        const {
            count: countAdded,
            componentsByType: addedByType,
            subComponentsDiff: addedSubComponents
        } = componentDifference({
            action: "added",
            componentType,
            componentsDiff: deletedComponents,
            modelData: curModelData,
            modelDataToCompare: prevModelData
        });

        numAdded += countAdded;
        if (!isEmpty(addedByType)) {
            addedComponents[componentType] = addedByType;
        }
        addedComponents = merge(addedComponents, addedSubComponents);
    });

    diffScore += numAdded + numDeleted;

    /* Changed Fields Check */
    const changedData = Object.entries(changeDetectionFields).reduce((acc, [key, { path, changePriority }]) => {
        var modelDataPath = path.replace("modelData.", "");

        const v1 = getObjValAtPath(prevModelData, modelDataPath);
        const v2 = getObjValAtPath(curModelData, modelDataPath);

        if (!isEqual(v1, v2)) {
            switch (changePriority) {
                case "HIGH":
                    diffScore += 10;
                    break;
                case "MED":
                    diffScore += 5;
                    break;
                case "LOW":
                    diffScore += 1;
                    break;
            }

            return {
                ...acc,
                [key]: { path: path, v1: v1, v2: v2 }
            };
        }
        return acc;
    }, {});

    return {
        changedData,
        diffScore,
        addedComponents,
        numAdded,
        deletedComponents,
        numDeleted,
    };
}